import {
  DataPointSearchAction,
  DataPointSearchActionType,
  DataPointSearchState,
  FilterState,
} from '../types';
import { getDependentFields } from '../utils';

export const initialFilterState: FilterState = {
  deviceType: '',
  parentDeviceId: '',
  deviceId: '',
  pointName: '',
};

export const initialState: DataPointSearchState = {
  filters: initialFilterState,
  gridFilters: [],
  searchText: '',
  activeSearch: '',
  pagination: { page: 0, pageSize: 25 },
  selection: new Map(),
  isModalOpen: false,
};

export const dataPointSearchReducer = (
  state: DataPointSearchState,
  action: DataPointSearchAction,
): DataPointSearchState => {
  switch (action.type) {
    case DataPointSearchActionType.SET_FILTER: {
      const { field, value } = action.payload;
      const dependentFields = getDependentFields(field);

      const clearedFilters = dependentFields.reduce(
        (acc, depField) => ({ ...acc, [depField]: '' }),
        {} as Partial<FilterState>,
      );

      return {
        ...state,
        filters: {
          ...state.filters,
          [field]: value,
          ...clearedFilters,
        },
        pagination: { ...state.pagination, page: 0 },
        selection: new Map(),
      };
    }

    case DataPointSearchActionType.SET_GRID_FILTERS:
      return {
        ...state,
        gridFilters: action.payload,
        pagination: { ...state.pagination, page: 0 },
        selection: new Map(),
      };

    case DataPointSearchActionType.CLEAR_FILTERS:
      return {
        ...state,
        filters: initialFilterState,
        gridFilters: [],
        pagination: { ...state.pagination, page: 0 },
        selection: new Map(),
      };

    case DataPointSearchActionType.SET_SEARCH_TEXT:
      return {
        ...state,
        searchText: action.payload,
      };

    case DataPointSearchActionType.SUBMIT_SEARCH:
      return {
        ...state,
        activeSearch: state.searchText ? `"${state.searchText}"` : '',
        pagination: { ...state.pagination, page: 0 },
        selection: new Map(),
      };

    case DataPointSearchActionType.CLEAR_SEARCH:
      return {
        ...state,
        searchText: '',
        activeSearch: '',
        pagination: { ...state.pagination, page: 0 },
        selection: new Map(),
      };

    case DataPointSearchActionType.SET_PAGINATION:
      return {
        ...state,
        pagination: { ...state.pagination, ...action.payload },
      };

    case DataPointSearchActionType.RESET_PAGINATION:
      return {
        ...state,
        pagination: { page: 0, pageSize: state.pagination.pageSize },
      };

    case DataPointSearchActionType.ADD_TO_SELECTION: {
      const newSelection = new Map(state.selection);
      action.payload.forEach((point) => {
        newSelection.set(point._id.$oid, point);
      });
      return {
        ...state,
        selection: newSelection,
      };
    }

    case DataPointSearchActionType.REMOVE_FROM_SELECTION: {
      const newSelection = new Map(state.selection);
      action.payload.forEach((id) => {
        newSelection.delete(id);
      });
      return {
        ...state,
        selection: newSelection,
      };
    }

    case DataPointSearchActionType.CLEAR_SELECTION:
      return {
        ...state,
        selection: new Map(),
      };

    case DataPointSearchActionType.SYNC_PAGE_SELECTION: {
      const { currentPageRows, selectedIds } = action.payload;
      const newSelection = new Map(state.selection);
      const selectedSet = new Set(selectedIds);
      const currentPageRowsById = new Map(
        currentPageRows.map((row) => [row._id.$oid, row]),
      );

      selectedIds.forEach((id) => {
        const row = currentPageRowsById.get(id);
        if (row) {
          newSelection.set(id, row);
        }
      });

      currentPageRowsById.forEach((_, id) => {
        if (!selectedSet.has(id) && state.selection.has(id)) {
          newSelection.delete(id);
        }
      });

      return {
        ...state,
        selection: newSelection,
      };
    }

    case DataPointSearchActionType.RESET_STATE:
      return initialState;

    default:
      return state;
  }
};
